import type { CSSProperties } from 'react'
import {
  labelDimensions, LABEL_OVERLAY_OFFSET_DEFAULT,
  CARD_TAG_LIMIT_DEFAULT, CARD_TAG_LIMIT_MAX, CARD_TAG_LIMIT_MIN, CARD_TAG_STYLE_DEFAULTS,
  type CardLabelFields, type CardTagsConfig, type LabelCorner, type LabelDimension, type LabelStyle, type TermLabelOverride,
} from '../../widgets/explorer/labels/schema'
import type { ExploreProperty } from '../../widgets/explorer/data'

/**
 * Label Designer v1 (#1069) resolver — pure. Mirrors the toolkit cascade
 * (Config::get_term_style → get_taxonomy_style → get_global_style, each a
 * key-by-key wp_parse_args merge) and CssGenerator's px emission, as inline
 * React styles instead of a generated stylesheet.
 *
 * Show semantics: the toolkit's per-term `show_label` flag is the override
 * row here — a term gets a badge iff it has a row; dimension/global defaults
 * only style, they never add badges (otherwise every amenity would badge).
 */
export type LabelConfig = {
  global?: LabelStyle
  defaults?: Partial<Record<LabelDimension, LabelStyle>>
  overrides: TermLabelOverride[]
}

/** Resolved overlay placement of one badge (#1370 slice 1). */
export type LabelOverlay = { corner: LabelCorner; offset: number }

export type CardLabel = {
  key: string
  text: string
  /** true = from an override row (inline style applies); false = the legacy primary-tag badge. */
  styled: boolean
  css?: CSSProperties
  iconUrl?: string
  /** true only when the cascaded style asks for it AND iconUrl is an .svg (#1370 slice 2): the icon is mask-tinted to the badge text colour. Absent otherwise, so a v1/v2 badge keeps its shape. */
  recolorIcon?: true
  /** true only when set AND an icon exists (#1370 slice 2): the text is visually hidden and kept as the accessible name. */
  iconOnly?: true
  /** Present only for overlay-placed badges (#1370); absent = the inline row, today's markup. */
  overlay?: LabelOverlay
}

/** Most badges the card overlays; the toolkit has no cap but the image corner is finite. */
export const CARD_LABELS_MAX = 6

/** Null until at least one override row exists — the no-config branch keeps today's card byte-for-byte. */
export function readLabelConfig(card: Partial<CardLabelFields> | undefined): LabelConfig | null {
  const overrides = card?.labelOverrides
  if (!Array.isArray(overrides) || overrides.length === 0) return null
  return { global: card?.labelGlobalDefault, defaults: card?.labelDefaults, overrides }
}

/** Later layers win, key by key; undefined leaves fall through (wp_parse_args). */
export function mergeLabelStyle(...layers: (LabelStyle | undefined)[]): LabelStyle {
  const out: Record<string, unknown> = {}
  for (const layer of layers) {
    if (!layer) continue
    for (const [k, v] of Object.entries(layer)) if (v !== undefined) out[k] = v
  }
  return out as LabelStyle
}

/** Cascaded style for one term, or null when the term has no override row. */
export function resolveTermLabelStyle(cfg: LabelConfig, dimension: LabelDimension, termId: number): LabelStyle | null {
  const row = cfg.overrides.find((o) => o.filterKey === dimension && o.centralId === termId)
  if (!row) return null
  return mergeLabelStyle(cfg.global, cfg.defaults?.[dimension], row.style)
}

/**
 * Black-or-white text for a hex background (YIQ ≥ 128 = light). Alpha digits
 * are ignored: a translucent badge sits on a photo, and the opaque colour is
 * the only stable signal. Keeps an operator-set bgColor legible in light and
 * dark schemes without a second pick.
 */
export function contrastTextColor(hex: string): '#111111' | '#ffffff' {
  let h = hex.replace('#', '')
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 128 ? '#111111' : '#ffffff'
}

/** Only set properties are emitted (the card CSS supplies the rest), px units like the toolkit. */
export function labelStyleToCss(style: LabelStyle): CSSProperties {
  const css: CSSProperties = {}
  if (style.bgColor) {
    css.backgroundColor = style.bgColor
    css.color = style.textColor ?? contrastTextColor(style.bgColor)
  } else if (style.textColor) {
    css.color = style.textColor
  }
  if (style.fontSize !== undefined) css.fontSize = `${style.fontSize}px`
  if (style.fontWeight !== undefined) css.fontWeight = Number(style.fontWeight)
  if (style.letterSpacing !== undefined) css.letterSpacing = `${style.letterSpacing}px`
  if (style.textTransform !== undefined) css.textTransform = style.textTransform
  if (style.borderRadius !== undefined) css.borderRadius = `${style.borderRadius}px`
  if (style.paddingH !== undefined || style.paddingV !== undefined) {
    css.padding = `${style.paddingV ?? 3}px ${style.paddingH ?? 5}px`
  }
  return css
}

/** Toolkit CssGenerator::build_icon_rule masks .svg icons only; raster icons paint as-is. */
export const SVG_ICON_URL_RE = /\.svg(\?|#|$)/i
export function isSvgIconUrl(url: string | undefined): boolean {
  return typeof url === 'string' && SVG_ICON_URL_RE.test(url)
}

/**
 * Icon flags for one cascaded style (#1370 slice 2; toolkit `recolor_icon` /
 * `icon_only`). Recolor applies to an .svg icon only — CssGenerator masks .svg
 * with currentColor and leaves raster icons alone even with the flag on;
 * icon-only needs an icon (the admin preview gates on `icon_only && icon_url`),
 * so a badge never collapses to nothing. Keys are added only when true so a
 * badge without them keeps the v1/v2 object shape. Unset = off: the toolkit's
 * recolor-on default is not materialised, because an existing feed with an
 * iconUrl must keep rendering the plain <img> it renders today.
 */
export function resolveLabelIconFlags(style: LabelStyle): Pick<CardLabel, 'recolorIcon' | 'iconOnly'> {
  const out: Pick<CardLabel, 'recolorIcon' | 'iconOnly'> = {}
  if (!style.iconUrl) return out
  if (style.recolorIcon && isSvgIconUrl(style.iconUrl)) out.recolorIcon = true
  if (style.iconOnly) out.iconOnly = true
  return out
}

/**
 * Inline mask for a tinted icon (#1370 slice 2): `-webkit-mask-image` first
 * (Safari < 15.4, Chrome < 120), then `mask-image` — CssGenerator's
 * `-webkit-mask` / `mask` pair. The URL is double-quoted with `"` and `\`
 * escaped and line breaks dropped, so an authored URL can end the string but
 * never the declaration. Repeat / position / size live in explorer-card.css.
 */
export function iconMaskStyle(iconUrl: string): CSSProperties {
  const url = `url("${iconUrl.replace(/[\r\n]/g, '').replace(/["\\]/g, '\\$&')}")`
  return { WebkitMaskImage: url, maskImage: url }
}

/** One `{id,name}` term as carried by the four array-shaped dimensions and by
 *  the (v2, #1364) singular `type`/`city` relations. */
type IdNameTerm = { id: number; name: string }

function isIdNameTerm(v: unknown): v is IdNameTerm {
  return (
    typeof v === 'object' && v !== null &&
    typeof (v as { id?: unknown }).id === 'number' && Number.isFinite((v as { id: number }).id) &&
    typeof (v as { name?: unknown }).name === 'string' && (v as { name: string }).name.length > 0
  )
}

/**
 * Terms for one dimension of a property (#1364, central#3137). groups / tags
 * / locations / amenities are already `{id,name}[]` and pass through
 * unchanged. types / cities are SINGULAR `{id,name}|null` relations on
 * `ExploreProperty` — wrapped into a one-element list when the value is a
 * conforming id-bearing term, and `[]` for null, undefined, a pre-#3137
 * name-only string, or any other non-conforming shape. Never throws, so an
 * older cached payload or a mid-rollout Central response degrades to no
 * badge for that dimension instead of bricking the card.
 */
export function termsForDimension(property: ExploreProperty, dimension: LabelDimension): IdNameTerm[] {
  if (dimension === 'types') return isIdNameTerm(property.type) ? [property.type] : []
  if (dimension === 'cities') return isIdNameTerm(property.city) ? [property.city] : []
  return property[dimension] ?? []
}

/**
 * Badges for one card: the primary tag first (styled if it has an override
 * row, else the legacy unstyled badge), then the remaining configured terms in
 * a fixed dimension order (groups, tags, locations, amenities, types, cities),
 * property term order within, capped. An operator who styles the primary tag
 * gets one badge, not two — and never zero.
 */
export function collectCardLabels(property: ExploreProperty, cfg: LabelConfig, cap: number = CARD_LABELS_MAX): CardLabel[] {
  const styled: CardLabel[] = []
  for (const dimension of labelDimensions) {
    for (const term of termsForDimension(property, dimension)) {
      const style = resolveTermLabelStyle(cfg, dimension, term.id)
      if (!style || !term.name) continue
      // The overlay key is added only when set, so an inline badge's shape is exactly the v1/v2 one.
      const overlay = resolveLabelOverlay(style)
      styled.push({ key: `${dimension}:${term.id}`, text: term.name, styled: true, css: labelStyleToCss(style), iconUrl: style.iconUrl, ...resolveLabelIconFlags(style), ...(overlay ? { overlay } : {}) })
    }
  }
  const out: CardLabel[] = []
  if (property.tag) {
    // Promotion is by IDENTITY: the primary tag is Central's tags[0], so only
    // that term's own styled badge takes the leading slot — never a same-named
    // Group, nor a second Tag term that happens to share the name (codex
    // closure 1 + 2). Suppression is by TEXT: the legacy badge exists only to
    // keep today's visible text on the card, so when any styled badge already
    // shows that text the unstyled duplicate is skipped (not promoted).
    const primary = property.tags?.[0]
    const primaryKey = primary && primary.name === property.tag ? `tags:${primary.id}` : undefined
    const own = primaryKey ? styled.findIndex((l) => l.key === primaryKey) : -1
    if (own >= 0) out.push(...styled.splice(own, 1))
    // Dedupe only against candidates that SURVIVE the cap: a namesake sliced
    // off below cannot stand in for the legacy text (codex closure 3).
    else if (!styled.slice(0, Math.max(0, cap)).some((l) => l.text === property.tag)) {
      // The legacy badge stays UNSTYLED (today's look), but it is a Tag term
      // without a row, so its PLACEMENT follows the same cascade a row-less tag
      // would get — global → Tag dimension default (#1370). Otherwise a global
      // "overlay" leaves this one badge behind in the row, where it collides
      // with a top-left overlay group at the same corner.
      const legacyOverlay = resolveLabelOverlay(mergeLabelStyle(cfg.global, cfg.defaults?.tags))
      out.push({ key: 'legacy-tag', text: property.tag, styled: false, ...(legacyOverlay ? { overlay: legacyOverlay } : {}) })
    }
  }
  return out.concat(styled).slice(0, Math.max(0, cap))
}

/**
 * Overlay placement for one cascaded style (#1370 slice 1), or null for the
 * inline row. The mode is explicit: `corner` / `offset` without
 * `placement: 'overlay'` change nothing. (The toolkit flips the whole row
 * into absolute mode on ANY non-default position — an implicit switch that
 * also stacks every default-positioned badge on the same 6px/6px pixel.)
 * Defaults mirror the toolkit's rendered default: top-left, 6px in.
 */
export function resolveLabelOverlay(style: LabelStyle): LabelOverlay | null {
  if (style.placement !== 'overlay') return null
  return { corner: style.corner ?? 'top-left', offset: style.offset ?? LABEL_OVERLAY_OFFSET_DEFAULT }
}

export type OverlayGroup = { key: string; corner: LabelCorner; offset: number; labels: CardLabel[] }

/**
 * Split a card's badges into the inline row and the overlay groups, both in
 * collection order. Badges sharing a corner AND offset share one group so
 * they flow with a gap instead of stacking on one pixel; a different offset
 * in the same corner is its own group. `canOverlay=false` (no photo to pin
 * to) keeps every badge in the row so a photo-less listing still shows its
 * labels.
 */
export function partitionCardLabels(labels: CardLabel[], canOverlay: boolean): { inline: CardLabel[]; overlayGroups: OverlayGroup[] } {
  const inline: CardLabel[] = []
  const overlayGroups: OverlayGroup[] = []
  for (const label of labels) {
    const overlay = canOverlay ? label.overlay : undefined
    if (!overlay) { inline.push(label); continue }
    const key = `${overlay.corner}:${overlay.offset}`
    let group = overlayGroups.find((g) => g.key === key)
    if (!group) {
      group = { key, corner: overlay.corner, offset: overlay.offset, labels: [] }
      overlayGroups.push(group)
    }
    group.labels.push(label)
  }
  return { inline, overlayGroups }
}

/**
 * Height of the slide-dot strip a multi-photo card paints at the bottom of the
 * photo (#1370 slice 2): a 12px dot (.hk-dotnav button) above the 12px
 * .hfse-property-slideshow-dotnav margin-bottom. Bottom-corner overlay groups
 * on such a card rise by this much so badges and dots stop sharing the strip;
 * the dots keep their place and their clicks, single-photo cards have no dots
 * and keep the exact offset. One dot row is assumed: a very wide photo set on
 * a narrow card wraps the dots, and that second row sits behind the badges as
 * before.
 */
export const LABEL_OVERLAY_DOTNAV_RESERVE = 24

/**
 * Inline insets for one overlay group. The corner picks top or bottom; BOTH
 * horizontal insets are set so the group spans the photo minus the offset on
 * each side, and the corner class (explorer-card.css) aligns the badges —
 * flex-end on the right, wrap-reverse at the bottom. Plain px only, no
 * calc() or custom properties, so the SSR attribute string and the CSR CSSOM
 * read back the same values. `reserveBottom` (#1370 slice 2) lifts a bottom
 * corner above the slide-dot strip; top corners ignore it.
 */
export function overlayGroupStyle({ corner, offset }: LabelOverlay, reserveBottom = 0): CSSProperties {
  const px = `${offset}px`
  return corner.startsWith('bottom') ? { bottom: `${offset + reserveBottom}px`, left: px, right: px } : { top: px, left: px, right: px }
}

// ── Card tags (#1812, toolkit Label Designer "Card tags" 3.326) ──────────────

/** One resolved pill: the term's card label (singular override or term name). */
export type CardTag = { key: string; text: string }

/**
 * The toolkit gate (CardTags::inject / enqueue_frontend / css all share it):
 * null unless the feature is on AND at least one term is listed, so a feed
 * that never enabled card tags — or enabled it and listed nothing — renders
 * today's type slot byte-for-byte.
 */
export function readCardTagsConfig(card: { cardTags?: CardTagsConfig | null } | undefined): CardTagsConfig | null {
  const cfg = card?.cardTags
  if (!cfg || cfg.enabled !== true) return null
  if (!Array.isArray(cfg.terms) || cfg.terms.length === 0) return null
  return cfg
}

/** The per-card cap: the authored limit inside the toolkit bounds, else 3 (CardTags::labels_for_slugs). */
export function cardTagLimit(cfg: Pick<CardTagsConfig, 'limit'>): number {
  const n = cfg.limit
  return typeof n === 'number' && Number.isInteger(n) && n >= CARD_TAG_LIMIT_MIN && n <= CARD_TAG_LIMIT_MAX ? n : CARD_TAG_LIMIT_DEFAULT
}

/**
 * CardTags::labels_for_slugs, pure: walk the CONFIGURED list in its order
 * (that order is the priority), keep the terms this home carries in the
 * source dimension, each under its singular card label (the term's own name
 * when the row has none), stop at the cap. A term listed twice counts once;
 * a listed term the home lacks is skipped, not blanked. Empty when the home
 * carries none of the listed terms — `keepType` decides what the slot shows
 * then, the caller's call.
 */
export function collectCardTags(property: Pick<ExploreProperty, 'tags' | 'amenities'>, cfg: CardTagsConfig): CardTag[] {
  const source = cfg.source === 'amenities' ? 'amenities' : 'tags'
  const have = new Map<number, string>()
  for (const term of property[source] ?? []) {
    if (isIdNameTerm(term) && !have.has(term.id)) have.set(term.id, term.name)
  }
  const limit = cardTagLimit(cfg)
  const out: CardTag[] = []
  const seen = new Set<number>()
  for (const row of cfg.terms ?? []) {
    const id = row.centralId
    if (seen.has(id)) continue
    const name = have.get(id)
    if (name === undefined) continue
    seen.add(id)
    const label = typeof row.label === 'string' ? row.label.trim() : ''
    out.push({ key: `${source}:${id}`, text: label || name })
    if (out.length >= limit) break
  }
  return out
}

/**
 * Inline style for one pill — CardTags::css() with the same defaults
 * (border 1px solid / padding / line-height ride in the stylesheet, this
 * carries only what the operator can set). Every value is emitted, so a
 * skin rule on the slot never restyles the pill behind the operator's back.
 */
export function cardTagPillStyle(cfg: Pick<CardTagsConfig, 'style'>): CSSProperties {
  const style = cfg.style ?? {}
  return {
    backgroundColor: style.bgColor ?? CARD_TAG_STYLE_DEFAULTS.bgColor,
    color: style.textColor ?? CARD_TAG_STYLE_DEFAULTS.textColor,
    borderColor: style.borderColor ?? CARD_TAG_STYLE_DEFAULTS.borderColor,
    borderRadius: `${style.borderRadius ?? CARD_TAG_STYLE_DEFAULTS.borderRadius}px`,
    fontSize: `${style.fontSize ?? CARD_TAG_STYLE_DEFAULTS.fontSize}px`,
  }
}

/**
 * The same pill style as a `style="…"` attribute value, for the string-HTML
 * surfaces (the map popup, common/map/marker.ts). Built from
 * `cardTagPillStyle` so the two can never disagree; every value is a
 * schema-validated hex or a px number, nothing to escape.
 */
export function cardTagPillStyleAttr(cfg: Pick<CardTagsConfig, 'style'>): string {
  const s = cardTagPillStyle(cfg)
  return `background-color:${s.backgroundColor};color:${s.color};border-color:${s.borderColor};border-radius:${s.borderRadius};font-size:${s.fontSize}`
}
